import { useState } from 'react'
import { toast } from 'sonner'
import * as planningApi from '../../../../lib/api/v2/planning'
import type { PlanningReminderDto, SupplierOrderDto } from '../../../../lib/api/v2/planning'
import { addWorkingDays } from '../../../../lib/working-days'
import { cn } from '../../../../lib/utils'
import { todayIso } from './supplier-orders-helpers'

interface RemindersProps {
  projectId: string
  reminders: PlanningReminderDto[]
  /** Orders of the project (template chips « Relancer {fournisseur} » + subject attach). */
  orders: SupplierOrderDto[]
  onMutated: () => void
}

const frDate = (iso: string) => `${iso.split('-')[2]}/${iso.split('-')[1]}`

function dueLabel(r: PlanningReminderDto): { text: string; overdue: boolean } {
  const today = todayIso()
  if (r.dueDate < today) {
    const days = Math.round((new Date(`${today}T00:00Z`).getTime() - new Date(`${r.dueDate}T00:00Z`).getTime()) / 86_400_000)
    return { text: `en retard de ${days} j`, overdue: true }
  }
  return { text: r.dueDate === today ? "aujourd'hui" : `prévu le ${frDate(r.dueDate)}`, overdue: false }
}

/**
 * Reminders (SOLY-PO-001 MS-04) — action-first: the title IS the action (« Relancer Van Hoecke »).
 * Silent before the deadline (this list is the work plan); the Suivi picks them up at D (C9).
 * « Fait » is explicit and offers to chain « relance n°N+1 ».
 */
export function Reminders({ projectId, reminders, orders, onMutated }: RemindersProps) {
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState<null | { title: string; description: string; due: string; orderId: string | null; chainIndex: number }>(null)
  const [rescheduling, setRescheduling] = useState<string | null>(null)
  const [rescheduleDate, setRescheduleDate] = useState(todayIso())

  const open = reminders.filter((r) => !r.completedAt)

  if (open.length === 0 && form === null) return null

  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true)
    try { await action(); toast.success(ok); setForm(null); setRescheduling(null); onMutated() }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Action échouée') }
    finally { setBusy(false) }
  }

  const complete = async (r: PlanningReminderDto) => {
    setBusy(true)
    try {
      await planningApi.completeReminder(r.id)
      toast.success('Rappel fait')
      // Chain proposal: prefill « relance n°N+1 » on the same subject.
      setForm({ title: r.title, description: r.description ?? '', due: addWorkingDays(todayIso(), 5), orderId: r.supplierOrderId, chainIndex: r.chainIndex + 1 })
      onMutated()
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Action échouée') }
    finally { setBusy(false) }
  }

  const submit = () => {
    if (!form || !form.title.trim()) return
    void run(() => planningApi.createReminder({
      title: form.title.trim(),
      description: form.description.trim() || null,
      dueDate: form.due,
      // Subject: the chosen order, else the project.
      supplierOrderId: form.orderId,
      projectId: form.orderId ? null : projectId,
      chainIndex: form.chainIndex,
    }), form.chainIndex > 1 ? `Relance n°${form.chainIndex} planifiée` : 'Rappel créé')
  }

  return (
    <div className="space-y-1.5" data-testid="planning-reminders">
      {open.map((r) => {
        const due = dueLabel(r)
        return (
          <div key={r.id} className="rounded-md border border-dashed p-1.5" data-testid={`planning-reminder-${r.id}`}>
            <div className="flex items-center gap-2">
              <span aria-hidden>⏰</span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium">
                  {r.title}{r.chainIndex > 1 && <span className="ml-1 text-[10px] text-muted-foreground">· relance n°{r.chainIndex}</span>}
                </div>
                <div className={cn('truncate text-[11px]', due.overdue ? 'font-medium text-red-600' : 'text-muted-foreground')}>
                  {due.text}{r.description ? ` — ${r.description}` : ''}
                </div>
              </div>
              <button type="button" disabled={busy} data-testid={`planning-reminder-done-${r.id}`}
                onClick={() => void complete(r)}
                className="flex-none rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Fait</button>
              <button type="button" disabled={busy} data-testid={`planning-reminder-reschedule-${r.id}`}
                onClick={() => { setRescheduling(rescheduling === r.id ? null : r.id); setRescheduleDate(r.dueDate) }}
                className="flex-none rounded border px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted">Replanifier</button>
            </div>
            {rescheduling === r.id && (
              <div className="mt-1 flex items-center gap-1.5 pl-6 text-[11px]">
                <input type="date" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} className="rounded border px-2 py-1 text-xs" data-testid={`planning-reminder-reschedule-date-${r.id}`} />
                <button type="button" disabled={busy || !rescheduleDate} data-testid={`planning-reminder-reschedule-submit-${r.id}`}
                  onClick={() => void run(() => planningApi.rescheduleReminder(r.id, rescheduleDate), 'Rappel replanifié')}
                  className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50">OK</button>
              </div>
            )}
          </div>
        )
      })}

      {form && (
        <div className="space-y-1.5 rounded-md border bg-muted/30 p-2" data-testid="planning-reminder-form">
          {form.chainIndex === 1 && (
            <div className="flex flex-wrap gap-1">
              {orders.map((o) => (
                <button key={o.id} type="button" data-testid={`planning-reminder-template-${o.id}`}
                  onClick={() => setForm({ ...form, title: `Relancer ${o.supplierName}`, orderId: o.id })}
                  className="rounded border px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted">Relancer {o.supplierName}</button>
              ))}
              <button type="button" data-testid="planning-reminder-template-info"
                onClick={() => setForm({ ...form, title: 'Demander les infos au vendeur', orderId: null })}
                className="rounded border px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted">Demander les infos</button>
            </div>
          )}
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="L'action, à l'impératif (ex: Relancer Van Hoecke)" className="w-full rounded border px-2 py-1 text-xs" data-testid="planning-reminder-title" autoFocus />
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Contexte (ex: doit m'envoyer la confirmation)" className="w-full rounded border px-2 py-1 text-xs" data-testid="planning-reminder-desc" />
          <div className="flex items-center gap-1.5 text-[11px]">
            <span className="text-muted-foreground">Pour le</span>
            <input type="date" value={form.due} onChange={(e) => setForm({ ...form, due: e.target.value })} className="rounded border px-2 py-1 text-xs" data-testid="planning-reminder-due" />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setForm(null)} data-testid="planning-reminder-cancel" className="rounded px-2 py-1 text-xs text-muted-foreground">Annuler</button>
            <button type="button" disabled={busy || !form.title.trim() || !form.due} onClick={submit} data-testid="planning-reminder-submit"
              className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground disabled:opacity-50">
              {form.chainIndex > 1 ? `Planifier la relance n°${form.chainIndex}` : 'Créer le rappel'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
